import { NO, UIColor, UIDialogView, UITextView, UIView, YES } from "uicore-ts"
import type {
    RHProcurementCopyListRow,
    RHProcurementLifecycleState,
    RHProcurementSourceSelectionMode,
    RHProcurementSourceSelectionResult
} from "../SocketClientFunctions"
import { RHCore } from "../Core/RHCore"
import { RHProcurementStore } from "../Core/RHProcurementStore"
import { CBDataViewCellDescriptorWithObject } from "../Custom components/CB/CBDataView"
import { RHAlertBannerView } from "../Custom components/RH/RHAlertBannerView"
import { RHButton } from "../Custom components/RH/RHButton"
import { RHColor } from "../Custom components/RH/RHColor"
import { RHDataView, RHNumberSorting } from "../Custom components/RH/RHDataView"
import { RHFilterChipBarView } from "../Custom components/RH/RHFilterChipBarView"
import { RHFilterChipButton } from "../Custom components/RH/RHFilterChipButton"
import {
    RHDateRangeCriterion,
    RHFilterCriteria,
    RHFilterPanelView,
    RHRangeCriterion
} from "../Custom components/RH/RHFilterPanelView"
import { RHTableActionButtonView } from "../Custom components/RH/RHTableActionButtonView"
import { formatRHDateTimeWithLineBreak } from "../Custom components/RH/Utils_slop/RHDateFormatting"
import { RHBadges, RHButtons } from "../Custom components/RH/Utils_slop/RHDesignTokens"
import { RHEuroFormatter } from "../Custom components/RH/Utils_slop/RHEuroFormatter"
import { RHProcurementLifecyclePresentation } from "../Custom components/RH/Utils_slop/RHProcurementLifecyclePresentation"
import { RHTextSanitizer } from "../Custom components/RH/Utils_slop/RHTextSanitizer"
import { RHCopyProcurementDialogViewTokens } from "../Custom components/RH/Utils_slop/RHViewTokens"


type SearchableProcurementCopyListRow = RHProcurementCopyListRow & {
    searchText: string
}


export class CopyProcurementDialogView extends UIDialogView {

    readonly contentView = new UIView()
        .configuredWithObject({
            backgroundColor: RHCopyProcurementDialogViewTokens.dialogBackgroundColor,
            style: {
                borderRadius: RHCopyProcurementDialogViewTokens.dialogBorderRadius,
                boxShadow: RHCopyProcurementDialogViewTokens.dialogBoxShadow
            }
        })
        .addedAsSubviewToView(this.view)

    readonly titleLabel = new UITextView(
        undefined,
        RHCopyProcurementDialogViewTokens.titleTextViewType
    ).configuredWithObject({
        text: "Kopeeri varasem hange",
        textColor: RHCopyProcurementDialogViewTokens.titleTextColor
    }).addedAsSubviewToView(this.contentView)

    readonly closeButton = new RHButton()
        .configuredWithObject({
            titleLabel: { text: "✕" }
        })
        .addedAsSubviewToView(this.contentView)

    // ── Lifecycle-state filter chips (same lifecycle filters as the procurer "Töölaud") ──
    readonly filterAllButton        = CopyProcurementDialogView._makeFilterChip("Kõik")
    readonly filterStartedButton    = CopyProcurementDialogView._makeFilterChip("Alustatud")
    readonly filterEvaluatingButton = CopyProcurementDialogView._makeFilterChip("Hindamisel")
    readonly filterFinalizedButton  = CopyProcurementDialogView._makeFilterChip("Lõpetatud")
    readonly filterInExecutionButton = CopyProcurementDialogView._makeFilterChip("Täitmisel")
    readonly filterExecutedButton   = CopyProcurementDialogView._makeFilterChip("Teostatud")
    // Drafts/templates never match a lifecycle category, so they need their own chips.
    readonly filterDraftsButton     = CopyProcurementDialogView._makeFilterChip("Ettevalmistamisel")
    // "Own" is procurement-team membership (see isOwnProcurement on
    // RHProcurementCopyListItem), not just same-company — a company-mate off the
    // team, or another company's publicly visible source (already limited to
    // public fields server-side, see ProcurementsController._procurementCopySourceForCompany),
    // both stay hidden by default until explicitly opened.
    readonly filterOwnProcurementsOnlyButton = CopyProcurementDialogView._makeFilterChip("Ainult minu hanked")
    readonly moreFiltersButton      = CopyProcurementDialogView._makeFilterChip("Muud filtrid")

    readonly filterChipBar = new RHFilterChipBarView(this.elementID + "FilterChipBar")
        .addedAsSubviewToView(this.contentView)

    readonly filterPanel = new RHFilterPanelView(this.elementID + "FilterPanel")
        .configuredWithObject({
            usesExternalToggle: YES,
            fields: [
                { key: "estimatedCost", label: "Eeldatav maksumus (KM-ta)", kind: "numberRange", allowDecimals: YES },
                { key: "publishedAt", label: "Avaldatud", kind: "dateRange" },
                { key: "deadlineDate", label: "Tähtaeg", kind: "dateRange" },
                {
                    key: "registeredTenderers", label: "Registreerunud pakkujaid",
                    kind: "numberRange", fromPlaceholder: "Min", toPlaceholder: "Max"
                },
                {
                    key: "offerCount", label: "Pakkumusi",
                    kind: "numberRange", fromPlaceholder: "Min", toPlaceholder: "Max"
                }
            ]
        }).addedAsSubviewToView(this.contentView)

    readonly procurementsDataView = new RHDataView<SearchableProcurementCopyListRow>(
        this.elementID + "ProcurementsDataView"
    ).configuredWithObject({
        isSearchAvailable: YES
    }).addedAsSubviewToView(this.contentView)

    readonly errorBanner = new RHAlertBannerView({
        alertType: RHAlertBannerView.type.Error,
        text: "Allikate laadimine ebaõnnestus."
    })

    readonly retryButton = new RHButton().configuredWithObject({
        titleLabel: { text: "Proovi uuesti" },
        variant: RHButtons.compactPrimaryVariant,
        contentPadding: RHButtons.compactContentPadding
    })

    readonly cancelButton = new RHButton()
        .configuredWithObject({
            titleLabel: { text: "Sulge" }
        })
        .addedAsSubviewToView(this.contentView)

    _allProcurements: SearchableProcurementCopyListRow[] = []
    private _filterCriteria: RHFilterCriteria = {}
    // Multi-select status chips: empty = "Kõik". A row matches if its lifecycle
    // matches ANY selected category (OR-logic) — same model as the dashboard.
    _activeLifecycleStates = new Set<RHProcurementLifecycleState>()
    // ORed together with _activeLifecycleStates (see _procurementMatchesLifecycleStateFilter).
    _showsDraftsOnly = NO
    // Independent of the lifecycle chips and never cleared by "Kõik" — see show() for the
    // per-mode default (its own toggle chip is hidden outside "previousProcurement" mode).
    _showsOwnProcurementsOnly = YES
    private _loadFailed = NO
    private _isSelectingProcurement = NO
    _mode: RHProcurementSourceSelectionMode = "previousProcurement"
    _companyId = ""
    _onProcurementSelected?: (selection: RHProcurementSourceSelectionResult) => Promise<void>


    constructor(elementID?: string) {
        super(elementID)

        this.view = this.contentView
        this.dismissesOnTapOutside = YES

        // Dialog chrome buttons are plain RHButtons (same pattern as CPVTreeDialogView),
        // NOT RHTableActionButtonView — that wrapper is only for in-table row actions.
        this.closeButton.configureWithObject({
            titleLabel: {
                text: RHButtons.dialogCloseTitle,
                style: {
                    fontSize: RHButtons.dialogCloseFontSize,
                    lineHeight: RHButtons.dialogCloseLineHeight
                }
            },
            variant: RHButtons.dialogCloseVariant,
            contentPadding: RHButtons.dialogCloseContentPadding,
            style: {
                padding: RHButtons.dialogClosePadding,
                borderRadius: RHButtons.dialogCloseBorderRadius
            }
        })
        this.closeButton.controlEventTargetAccumulator.PrimaryActionTriggered = () => this.dismiss()

        this.cancelButton.configureWithObject({
            titleLabel: {
                style: { fontSize: RHCopyProcurementDialogViewTokens.footerButtonFontSize }
            },
            variant: RHCopyProcurementDialogViewTokens.footerButtonVariant,
            contentPadding: RHCopyProcurementDialogViewTokens.footerButtonContentPadding
        })
        this.cancelButton.controlEventTargetAccumulator.PrimaryActionTriggered = () => this.dismiss()

        // Multi-select status chips + the "Muud filtrid" panel toggle, exactly like
        // the procurer dashboard. "Kõik" clears all; clicking an active chip removes it.
        this.filterChipBar.chips = this._visibleLifecycleStateFilterButtons()
        this.filterAllButton.controlEventTargetAccumulator.PrimaryActionTriggered = () => {
            this._clearLifecycleStateFilters()
        }
        for (const chip of this._lifecycleStateChipDefinitions()) {
            chip.button.controlEventTargetAccumulator.PrimaryActionTriggered = () => {
                this._toggleLifecycleStateFilter(chip.states)
            }
        }
        this.filterDraftsButton.controlEventTargetAccumulator.PrimaryActionTriggered = () => {
            this._toggleDraftsOnlyFilter()
        }
        this.filterOwnProcurementsOnlyButton.controlEventTargetAccumulator.PrimaryActionTriggered = () => {
            this._toggleOwnProcurementsOnlyFilter()
        }

        this.moreFiltersButton.titleLabel.text = CopyProcurementDialogView._moreFiltersLabelMarkup(NO)
        this.moreFiltersButton.controlEventTargetAccumulator.PrimaryActionTriggered = () => {
            this.filterPanel.isExpanded = NO == this.filterPanel.isExpanded
        }
        this.filterPanel.isExpandedDidChange = expanded => {
            this.moreFiltersButton.chipOpen = expanded
            this.moreFiltersButton.titleLabel.text = CopyProcurementDialogView._moreFiltersLabelMarkup(expanded)
            this.filterChipBar.setNeedsLayout()
            this.setNeedsLayout()
        }

        this.filterPanel.filterCriteriaDidChange = criteria => {
            this._filterCriteria = criteria
            this._applyFilters()
        }

        this.procurementsDataView.didUpdateTableAfterSearchTextChange = () => {
            this._updateNoDataMessage()
        }
        this.retryButton.controlEventTargetAccumulator.PrimaryActionTriggered = () => {
            void this._loadSources()
        }

        this._setupDescriptors()
        this._applyTableColumnLayout()
        this._updateChipsAppearance()
        this._updateNoDataMessage()
    }


    async show(
        mode: RHProcurementSourceSelectionMode,
        companyId: string,
        onProcurementSelected: (selection: RHProcurementSourceSelectionResult) => Promise<void>
    ) {
        this._mode = mode
        this._companyId = companyId
        this._onProcurementSelected = onProcurementSelected
        if (mode === "template") {
            this.titleLabel.text = "Alusta mallist"
        }
        else {
            this.titleLabel.text = "Kopeeri varasem hange"
        }
        // Templates are company-wide, not team-scoped, and their toggle chip is hidden in
        // this mode — defaulting ON here would hide every template with no way to undo it.
        this._showsOwnProcurementsOnly = mode === "previousProcurement"
        this._clearLifecycleStateFilters()
        this.filterChipBar.chips = this._visibleLifecycleStateFilterButtons()
        this._loadFailed = NO
        this._isSelectingProcurement = NO
        this._allProcurements = []
        this._filterCriteria = {}
        this.procurementsDataView.data = []
        this.procurementsDataView.loading = YES
        this.procurementsDataView.searchTextField.text = ""
        this.filterPanel.isExpanded = NO

        this.showInRootView(YES)

        await this._loadSources()
    }

    async _loadSources() {
        this._loadFailed = NO
        this.errorBanner.removeFromSuperview()
        this.retryButton.removeFromSuperview()
        this.procurementsDataView.loading = YES
        this.procurementsDataView.data = []
        try {
            const procurements = await RHCore.sharedInstance.procurementStore.retrieveProcurementSources(
                this._mode,
                this._companyId
            )
            this._allProcurements = procurements.map(procurement => {
                const displayTitle = procurement.templateName || procurement.title
                return {
                    ...procurement,
                    searchText: [
                        displayTitle,
                        procurement.title,
                        procurement.referenceNumber
                    ].filter(value => !!value).join(" "),
                    ...RHNumberSorting.fields(RHProcurementStore.totalEstimatedCost(procurement))
                }
            })
            this._applyFilters()
        }
        catch (error) {
            console.error("CopyProcurementDialogView: failed to load copyable procurements", error)
            this._loadFailed = YES
            this.procurementsDataView.data = []
            this.errorBanner.addedAsSubviewToView(this.contentView)
            this.retryButton.addedAsSubviewToView(this.contentView)
            this._updateNoDataMessage()
        }
        finally {
            this.procurementsDataView.loading = NO
            this.setNeedsLayoutUpToRootView()
            this.procurementsDataView.searchTextField.focus()
        }
    }


    private _setupDescriptors() {
        this.procurementsDataView.descriptors = [
            CBDataViewCellDescriptorWithObject({
                title: "Viitenumber",
                keyPath: "referenceNumber",
                allowSorting: YES,
                titleLabelAlignment: UITextView.textAlignment.left,
                cellLabelAlignment: UITextView.textAlignment.left,
                updateCell: (cellView, dataItem) => {
                    cellView.text = dataItem.referenceNumber || "-"
                }
            }),
            CBDataViewCellDescriptorWithObject({
                title: "Nimetus",
                keyPath: "searchText",
                minimumWidth: RHCopyProcurementDialogViewTokens.columnMinimumWidths.title,
                allowSorting: YES,
                titleLabelAlignment: UITextView.textAlignment.left,
                cellLabelAlignment: UITextView.textAlignment.left,
                updateCell: (cellView, dataItem) => {
                    cellView.text = RHTextSanitizer.escapedHTML(
                        dataItem.templateName || dataItem.title || "Nimetus puudub"
                    )
                    cellView.configureAsClampedMultilineText()
                }
            }),
            CBDataViewCellDescriptorWithObject({
                title: "Ettevõte",
                keyPath: "procuringCompanyName",
                allowSorting: YES,
                titleLabelAlignment: UITextView.textAlignment.left,
                cellLabelAlignment: UITextView.textAlignment.left,
                updateCell: (cellView, dataItem) => {
                    cellView.text = RHTextSanitizer.escapedHTML(dataItem.procuringCompanyName || "-")
                    cellView.configureAsClampedMultilineText()
                }
            }),
            CBDataViewCellDescriptorWithObject({
                title: "Tüüp",
                keyPath: "sourceKind",
                minimumWidth: RHCopyProcurementDialogViewTokens.columnMinimumWidths.type,
                allowSorting: YES,
                titleLabelAlignment: UITextView.textAlignment.center,
                cellLabelAlignment: UITextView.textAlignment.center,
                updateCell: (cellView, dataItem) => {
                    const presentation = CopyProcurementDialogView._sourceKindPresentation(dataItem)
                    cellView.text = presentation.text
                    cellView.titleLabel!.textColor = presentation.color
                    cellView.configureAsMultilineTextPreservingLineBreaks()
                }
            }),
            CBDataViewCellDescriptorWithObject({
                title: "Seisund",
                keyPath: "lifecycleState",
                minimumWidth: RHCopyProcurementDialogViewTokens.columnMinimumWidths.status,
                allowSorting: YES,
                titleLabelAlignment: UITextView.textAlignment.center,
                cellLabelAlignment: UITextView.textAlignment.center,
                updateCell: (cellView, dataItem) => {
                    cellView.text = RHProcurementLifecyclePresentation.textForLifecycleState(
                        dataItem.lifecycleState
                    )
                    cellView.titleLabel!.textColor =
                        RHProcurementLifecyclePresentation.colorForLifecycleState(dataItem.lifecycleState)
                }
            }),
            CBDataViewCellDescriptorWithObject({
                // Neutral: drafts/templates show createdAt here, not a real publication date.
                title: "Kuupäev",
                keyPath: "publishedAt",
                allowSorting: YES,
                initialOrderingState: "descending",
                titleLabelAlignment: UITextView.textAlignment.center,
                cellLabelAlignment: UITextView.textAlignment.center,
                updateCell: (cellView, dataItem) => {
                    const date = CopyProcurementDialogView._displayDate(dataItem)
                    cellView.text = date ? formatRHDateTimeWithLineBreak(date) : "-"
                    cellView.titleLabel!.isSingleLine = NO
                }
            }),
            CBDataViewCellDescriptorWithObject({
                title: "Tähtaeg",
                keyPath: "deadlineDate",
                allowSorting: YES,
                titleLabelAlignment: UITextView.textAlignment.center,
                cellLabelAlignment: UITextView.textAlignment.center,
                updateCell: (cellView, dataItem) => {
                    cellView.text = dataItem.deadlineDate
                                    ? formatRHDateTimeWithLineBreak(new Date(dataItem.deadlineDate))
                                    : "-"
                    cellView.titleLabel!.isSingleLine = NO
                }
            }),
            CBDataViewCellDescriptorWithObject({
                title: "Maksumus (KM-ta)",
                keyPath: RHNumberSorting.valueKeyPath,
                allowSorting: YES,
                dataType: "number",
                sortingInstructions: orderingState => RHNumberSorting.instructions(orderingState),
                titleLabelAlignment: UITextView.textAlignment.right,
                cellLabelAlignment: UITextView.textAlignment.right,
                updateCell: (cellView, dataItem) => {
                    const costs = (dataItem.parts ?? [])
                        .map(part => part.estimatedCost)
                        .filter((cost): cost is number => typeof cost === "number")
                    cellView.text = costs.length === 0
                                    ? "-"
                                    : RHEuroFormatter.formattedAmount(costs.reduce((sum, cost) => sum + cost, 0))
                }
            }),
            CBDataViewCellDescriptorWithObject<RHProcurementCopyListRow, RHTableActionButtonView>({
                title: "Valik",
                keyPath: "_id",
                minimumWidth: RHCopyProcurementDialogViewTokens.columnMinimumWidths.selection,
                allowSorting: NO,
                titleLabelAlignment: UITextView.textAlignment.center,
                cellLabelAlignment: UITextView.textAlignment.center,
                initCustomView: () => {
                    const buttonView = new RHTableActionButtonView().configuredWithObject({
                        preferredButtonWidth: RHCopyProcurementDialogViewTokens.selectButtonWidth,
                        preferredButtonHeight: RHCopyProcurementDialogViewTokens.selectButtonHeight
                    })
                    buttonView.button.variant = RHCopyProcurementDialogViewTokens.selectButtonVariant
                    return buttonView
                },
                updateCell: (cellView, dataItem) => {
                    cellView.customView.text = CopyProcurementDialogView._selectionButtonText(dataItem)
                    cellView.customView.button.enabled = NO == this._isSelectingProcurement
                    cellView.customView.buttonWasPressed = () => {
                        this._selectProcurement(dataItem)
                    }
                }
            })
        ]
    }


    private _applyTableColumnLayout() {
        this.procurementsDataView.tableHeaderView.configureWithObject({
            cellWeights: RHCopyProcurementDialogViewTokens.columnWeights,
            intrinsicContentHeight: () => RHCopyProcurementDialogViewTokens.tableHeaderHeight
        })
        this.procurementsDataView.tableView.configureWithObject({
            cellWeights: RHCopyProcurementDialogViewTokens.columnWeights
        })
        this._configureTableHeaderCells()
        this.procurementsDataView.tableHeaderView.setNeedsLayout()
        this.procurementsDataView.tableView.setNeedsLayout()
    }


    // Tighten each header cell's sort-button content padding and allow wrapping so
    // the labels fit instead of truncating to "Av…" — the same treatment the main
    // procurement tables apply.
    private _configureTableHeaderCells() {
        for (const headerCell of this.procurementsDataView.tableHeaderView.cells) {
            const buttonView = headerCell.buttonView
            if (buttonView) {
                buttonView.contentPadding = RHCopyProcurementDialogViewTokens.headerContentPadding
            }
            const titleLabel = buttonView?.titleLabel ?? headerCell.titleLabel
            if (!titleLabel) {
                continue
            }
            titleLabel.isSingleLine = NO
            titleLabel.style.whiteSpace = "normal"
            titleLabel.style.lineHeight = RHCopyProcurementDialogViewTokens.headerLineHeight
            titleLabel.style.fontSize = RHCopyProcurementDialogViewTokens.headerFontSize
            titleLabel.style.letterSpacing = RHCopyProcurementDialogViewTokens.headerLetterSpacing
            titleLabel.textElementView.style.whiteSpace = "normal"
            titleLabel.textElementView.style.lineHeight = RHCopyProcurementDialogViewTokens.headerLineHeight
            titleLabel.textElementView.style.fontSize = RHCopyProcurementDialogViewTokens.headerFontSize
            titleLabel.textElementView.style.letterSpacing = RHCopyProcurementDialogViewTokens.headerLetterSpacing
        }
    }


    private _applyFilters() {
        this._updateChipCounts()
        this.procurementsDataView.data = this._allProcurements.filter(procurement =>
            this._procurementMatchesLifecycleStateFilter(procurement) &&
            this._procurementMatchesFilterCriteria(procurement)
        )
        this._updateNoDataMessage()
        this.setNeedsLayout()
    }


    // ── Lifecycle-state filter chip state (multi-select) ────────────────────────

    private static _makeFilterChip(title: string): RHFilterChipButton {
        return new RHFilterChipButton().configuredWithObject({
            titleLabel: { text: title }
        })
    }

    private static _moreFiltersLabelMarkup(expanded: boolean): string {
        let caret = RHBadges.filterChip.caretCollapsedMarkup
        if (expanded) {
            caret = RHBadges.filterChip.caretExpandedMarkup
        }
        return RHBadges.filterChip.funnelIconMarkup + "Muud filtrid" + caret
    }

    _lifecycleStateChipDefinitions(): {
        button: RHFilterChipButton,
        states: RHProcurementLifecycleState[],
        baseLabel: string
    }[] {
        return [
            { button: this.filterStartedButton, states: ["started"], baseLabel: "Alustatud" },
            { button: this.filterEvaluatingButton, states: ["evaluating"], baseLabel: "Hindamisel" },
            {
                button: this.filterFinalizedButton,
                states: ["awaitingContract", "closedWithoutContract"],
                baseLabel: "Lõpetatud"
            },
            { button: this.filterInExecutionButton, states: ["inExecution"], baseLabel: "Täitmisel" },
            { button: this.filterExecutedButton, states: ["executed"], baseLabel: "Teostatud" }
        ]
    }

    _visibleLifecycleStateFilterButtons() {
        const buttons = [
            this.filterAllButton,
            this.filterStartedButton,
            this.filterEvaluatingButton,
            this.filterFinalizedButton,
            this.filterInExecutionButton,
            this.filterExecutedButton,
        ]
        if (this._mode === "previousProcurement") {
            buttons.push(this.filterDraftsButton, this.filterOwnProcurementsOnlyButton)
        }
        buttons.push(this.moreFiltersButton)
        return buttons
    }

    _toggleLifecycleStateFilter(states: RHProcurementLifecycleState[]) {
        const allStatesAlreadyActive = states.every(state => this._activeLifecycleStates.has(state))
        for (const state of states) {
            if (allStatesAlreadyActive) {
                this._activeLifecycleStates.delete(state)
            }
            else {
                this._activeLifecycleStates.add(state)
            }
        }
        this._updateChipsAppearance()
        this._applyFilters()
    }

    _toggleDraftsOnlyFilter() {
        this._showsDraftsOnly = NO == this._showsDraftsOnly
        this._updateChipsAppearance()
        this._applyFilters()
    }

    // Deliberately not folded into _clearLifecycleStateFilters — "Kõik" must never
    // re-expose procurements outside the user's own team as a side effect of
    // clearing status chips.
    _toggleOwnProcurementsOnlyFilter() {
        this._showsOwnProcurementsOnly = NO == this._showsOwnProcurementsOnly
        this._updateChipsAppearance()
        this._applyFilters()
    }

    _clearLifecycleStateFilters() {
        this._activeLifecycleStates.clear()
        this._showsDraftsOnly = NO
        this._updateChipsAppearance()
        this._applyFilters()
    }

    private _updateChipsAppearance() {
        this.filterAllButton.chipActive = this._activeLifecycleStates.size === 0 && NO == this._showsDraftsOnly
        for (const chip of this._lifecycleStateChipDefinitions()) {
            chip.button.chipActive = chip.states.every(state => this._activeLifecycleStates.has(state))
        }
        this.filterDraftsButton.chipActive = this._showsDraftsOnly
        this.filterOwnProcurementsOnlyButton.chipActive = this._showsOwnProcurementsOnly
    }

    private _updateChipCounts() {
        const matchingCriteria = this._allProcurements.filter(
            procurement => this._procurementMatchesFilterCriteria(procurement)
        )
        this.filterAllButton.titleLabel.text = this._chipLabelWithCount("Kõik", matchingCriteria.length)
        for (const chip of this._lifecycleStateChipDefinitions()) {
            const count = matchingCriteria.filter(
                procurement => chip.states.includes(procurement.lifecycleState)
            ).length
            chip.button.titleLabel.text = this._chipLabelWithCount(chip.baseLabel, count)
        }
        const draftCount = matchingCriteria.filter(
            procurement => procurement.sourceKind === "draft"
        ).length
        this.filterDraftsButton.titleLabel.text = this._chipLabelWithCount("Ettevalmistamisel", draftCount)
        this.filterChipBar.setNeedsLayout()
    }

    // Count appended as PLAIN text (no HTML span — see RHFilterChipButton sizing).
    private _chipLabelWithCount(label: string, count: number): string {
        return `${label} · ${count}`
    }

    _procurementMatchesLifecycleStateFilter(procurement: RHProcurementCopyListRow): boolean {
        if (this._activeLifecycleStates.size === 0 && NO == this._showsDraftsOnly) {
            return YES
        }
        if (this._showsDraftsOnly && procurement.sourceKind === "draft") {
            return YES
        }
        if (this._activeLifecycleStates.has(procurement.lifecycleState)) {
            return YES
        }
        return NO
    }


    _procurementMatchesFilterCriteria(procurement: RHProcurementCopyListRow): boolean {
        if (this._showsOwnProcurementsOnly && NO == procurement.isOwnProcurement) {
            return NO
        }

        const criteria = this._filterCriteria

        if (!RHFilterPanelView.matchesNumberRange(
            CopyProcurementDialogView._estimatedCost(procurement),
            criteria.estimatedCost as RHRangeCriterion
        )) {
            return NO
        }

        const publishedAt = CopyProcurementDialogView._displayDate(procurement)
        if (!RHFilterPanelView.matchesDateRange(publishedAt, criteria.publishedAt as RHDateRangeCriterion)) {
            return NO
        }

        const deadlineDate = procurement.deadlineDate ? new Date(procurement.deadlineDate) : undefined
        if (!RHFilterPanelView.matchesDateRange(deadlineDate, criteria.deadlineDate as RHDateRangeCriterion)) {
            return NO
        }

        if (!RHFilterPanelView.matchesNumberRange(
            procurement.registeredTendererCount,
            criteria.registeredTenderers as RHRangeCriterion
        )) {
            return NO
        }

        if (!RHFilterPanelView.matchesNumberRange(
            procurement.offerCount,
            criteria.offerCount as RHRangeCriterion
        )) {
            return NO
        }

        return YES
    }


    private _updateNoDataMessage() {
        if (this._loadFailed) {
            this.procurementsDataView.noDataMessage = "Allikate laadimine ebaõnnestus."
            return
        }

        const noSourcesMessage = this._mode === "template"
            ? "Aktiivseid hankemalle pole."
            : "Kopeerimiseks sobivaid hankeid pole."

        if (this._allProcurements.length === 0) {
            this.procurementsDataView.noDataMessage = noSourcesMessage
            return
        }

        const hasSearchText = this.procurementsDataView.searchTextField.text.trim().length > 0
        const hasFilterCriteria = Object.keys(this._filterCriteria).length > 0
        if ((hasSearchText || hasFilterCriteria) && this.procurementsDataView.filteredData.length === 0) {
            this.procurementsDataView.noDataMessage = "Filtrile vastavaid hankeid pole."
            return
        }

        // Chip-driven filters (own-only, lifecycle, drafts) aren't tracked in
        // _filterCriteria, so an all-chips-excluded empty result also lands here.
        this.procurementsDataView.noDataMessage = noSourcesMessage
    }


    async _selectProcurement(procurement: RHProcurementCopyListRow) {
        const procurementId = procurement._id?.toString()
        if (!procurementId || this._isSelectingProcurement) {
            return
        }

        this._isSelectingProcurement = YES
        this._applyFilters()

        try {
            const result = await RHCore.sharedInstance.procurementStore.resolveProcurementSourceSelection({
                mode: this._mode,
                sourceId: procurementId,
                companyId: this._companyId,
                expectedTemplateUpdatedAt: procurement.templateUpdatedAt
                    ? new Date(procurement.templateUpdatedAt).toISOString()
                    : undefined
            })
            if (!result.success) {
                if (
                    result.errorCode === "TEMPLATE_ARCHIVED" ||
                    result.errorCode === "TEMPLATE_NOT_FOUND" ||
                    result.errorCode === "TEMPLATE_VERSION_CONFLICT"
                ) {
                    await this._loadSources()
                }
                this._showSelectionError(result.errorCode)
                return
            }
            await this._onProcurementSelected?.(result.value)
            // Keep the dialog in place until the asynchronous form hydration has
            // restored the copied part invitations. The next form action must
            // never observe the partially applied copy state.
            this.dismiss()
        }
        catch (error) {
            console.error("CopyProcurementDialogView: failed to load copy source", error)
            this._showSelectionError()
        }
        finally {
            this._isSelectingProcurement = NO
            this._applyFilters()
        }
    }


    _showSelectionError(errorCode?: string) {
        let text = "Hanke andmete kopeerimine ebaõnnestus. Proovi uuesti."
        if (errorCode === "TEMPLATE_ARCHIVED") {
            text = "Hankemall on arhiveeritud. Värskenda loendit ja vali aktiivne mall."
        }
        else if (errorCode === "TEMPLATE_VERSION_CONFLICT") {
            text = "Hankemall on vahepeal muutunud. Värskenda loendit ja proovi uuesti."
        }
        else if (errorCode === "TEMPLATE_NOT_FOUND") {
            text = "Hankemalli ei leitud. Loend on värskendatud."
        }
        this.errorBanner.configureBannerWithObject({ text })
        this.errorBanner.addedAsSubviewToView(this.contentView)
        this.retryButton.addedAsSubviewToView(this.contentView)
        this.setNeedsLayoutUpToRootView()
    }


    static _sourceKindPresentation(
        procurement: RHProcurementCopyListRow
    ): { text: string, color: UIColor } {
        if (procurement.sourceKind === "template") {
            return { text: "Mall", color: RHColor.textBlack }
        }
        if (procurement.sourceKind === "draft") {
            return { text: "Ettevalmistamisel", color: RHColor.textMuted }
        }
        return { text: "Varasem hange", color: RHColor.textBlack }
    }


    static _selectionButtonText(procurement: RHProcurementCopyListRow): string {
        if (procurement.sourceKind === "template") {
            return "Alusta mallist"
        }
        if (procurement.sourceKind === "draft") {
            return "Kopeeri hange"
        }
        return "Kopeeri hange"
    }


    static _estimatedCost(procurement: RHProcurementCopyListRow): number {
        return (procurement.parts ?? [])
            .map(part => part.estimatedCost)
            .filter((cost): cost is number => typeof cost === "number")
            .reduce((sum, cost) => sum + cost, 0)
    }


    static _displayDate(procurement: RHProcurementCopyListRow): Date | undefined {
        const value = procurement.publishedAt ?? procurement.createdAt
        return value ? new Date(value) : undefined
    }


    override layoutSubviews() {
        super.layoutSubviews()

        const tokens = RHCopyProcurementDialogViewTokens
        const dialogBounds = this.bounds
        const closeButtonSize = RHButtons.dialogCloseSize
        const dialogWidth = Math.min(tokens.dialogMaxWidth, dialogBounds.width * tokens.dialogWidthViewportRatio)
        const maxDialogHeight = Math.max(0, dialogBounds.height - tokens.dialogViewportMargin * 2)
        const tableHeight = Math.min(
            tokens.tableHeight,
            Math.max(tokens.tableMinHeight, dialogBounds.height * tokens.tableMaxViewportRatio)
        )

        this.contentView.frame = dialogBounds
            .rectangleWithWidth(dialogWidth, 0.5)
            .rectangleWithHeight(0)

        const bounds = this.contentView.contentBoundsWithInset(tokens.contentInset)
        const headerRowFrame = bounds.rectangleWithHeight(tokens.headerHeight)

        headerRowFrame
            .rectangleWithWidth(headerRowFrame.width - closeButtonSize - tokens.contentInset)
            .assignedAsFrameOfView(this.titleLabel)
            .rectangleForNextColumn(tokens.contentInset, closeButtonSize)
            .rectangleWithHeight(closeButtonSize, 0.5)
            .assignedAsFrameOfView(this.closeButton)

        const chipBarFrame = headerRowFrame
            .rectangleForNextRow(tokens.titleToFilterGap, this.filterChipBar)
            .assignedAsFrameOfView(this.filterChipBar)

        // The "Muud filtrid" panel (external toggle) drops in below the chips, taking
        // 0 height while collapsed.
        const filterFrame = chipBarFrame
            .rectangleForNextRow(tokens.titleToFilterGap, this.filterPanel.intrinsicContentHeight(bounds.width))
            .assignedAsFrameOfView(this.filterPanel)

        let currentFrame = filterFrame
        if (this.errorBanner.superview === this.contentView) {
            currentFrame = currentFrame
                .rectangleForNextRow(tokens.filterToTableGap, this.errorBanner)
                .assignedAsFrameOfView(this.errorBanner)
            currentFrame = currentFrame
                .rectangleForNextRow(tokens.titleToFilterGap, tokens.footerButtonHeight)
            currentFrame
                .rectangleWithWidth(tokens.footerButtonWidth, 0)
                .assignedAsFrameOfView(this.retryButton)
        }

        const tableFrame = currentFrame
            .rectangleForNextRow(tokens.filterToTableGap, tableHeight)
            .assignedAsFrameOfView(this.procurementsDataView)

        const footerFrame = tableFrame
            .rectangleForNextRow(tokens.footerTopGap, tokens.footerButtonHeight)
            .rectangleWithWidth(tokens.footerButtonWidth, 0.5)
            .assignedAsFrameOfView(this.cancelButton)

        const finalHeight = Math.min(
            footerFrame.max.y + tokens.contentInset,
            maxDialogHeight || footerFrame.max.y + tokens.contentInset
        )

        let dialogFrame = dialogBounds
            .rectangleWithWidth(dialogWidth, 0.5)
            .rectangleWithHeight(finalHeight, 0.5)

        if (dialogFrame.y < tokens.dialogViewportMargin) {
            dialogFrame = dialogFrame.rectangleWithY(tokens.dialogViewportMargin)
        }

        this.contentView.frame = dialogFrame
    }

}
